import {
  createRootRouteWithContext,
  Outlet,
  useRouter,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { getDefaultStore } from "jotai";
import {
  authTokenAtom,
  activeTenantIdAtom,
  setAuthAtom,
  clearAuthAtom,
  markAuthUnavailableAtom,
} from "@tubo/store";
import { isAuthExpiredError, isTemporaryAuthFailure } from "@tubo/api-models/context/api-client";
import { Button } from "@tubo/ui/components/ui/button";
import { Refresh } from "@tubo/api-models/hooks/auth-api.hooks";
import { Me } from "@tubo/api-models/hooks/user-api.hooks";
import { useUiTranslation } from "@tubo/ui/i18n";

/** 与 App.tsx createRouter 注入的 context 对齐：路由 loader 可取 queryClient / api */
export interface RouterAppContext {
  api: AxiosInstance;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  beforeLoad: async () => {
    const store = getDefaultStore();
    if (store.get(authTokenAtom)) return;

    try {
      const activeTenantId = store.get(activeTenantIdAtom);
      // 带租户 id 刷新：保持上次切换的租户上下文；
      // 若该租户已失效（如用户被移出）则回退默认租户，避免直接登出
      const refreshData = await Refresh(
        activeTenantId ? { tenantId: activeTenantId } : undefined,
      ).catch((err: unknown) =>
        activeTenantId && !isAuthExpiredError(err) && !isTemporaryAuthFailure(err)
          ? Refresh() : Promise.reject(err),
      );

      const newToken = refreshData.token?.accessToken;
      if (!newToken) {
        throw new Error("会话刷新未返回访问令牌");
      }

      // 先设置 token，以便后续 /intl/user/me 请求携带 Authorization header
      store.set(authTokenAtom, newToken);

      const meData = await Me();
      store.set(setAuthAtom, { token: newToken, me: meData });
    } catch (error: unknown) {
      if (isAuthExpiredError(error)) {
        store.set(clearAuthAtom);
        return;
      }
      store.set(authTokenAtom, null);
      if (isTemporaryAuthFailure(error)) store.set(markAuthUnavailableAtom);
      throw error;
    }
  },
  // root 的 beforeLoad 是异步鉴权刷新（Refresh/Me）。router 级 defaultPendingMs=0
  // 会给每个带 beforeLoad 的 match 武装立即提交的 pendingTimeout，root 若参与会在
  // 鉴权完成前提交整棵路由树（闪登录页/未授权态），故 Infinity 退出 pending 提交。
  pendingMs: Infinity,
  component: RootComponent,
  errorComponent: RootLoadError,
  notFoundComponent: RootNotFound,
});

function RootLoadError() {
  const router = useRouter();
  const { t } = useUiTranslation("auth");
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
      <p className="text-muted-foreground">{t("pageLoadFailed")}</p>
      <Button onClick={() => void router.invalidate()}>{t("retry")}</Button>
    </div>
  );
}

function RootNotFound() {
  const router = useRouter();
  const { t } = useUiTranslation("common");
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-xl font-semibold">{t("pageNotFound")}</h1>
      <p className="text-muted-foreground">{t("pageNotFoundDescription")}</p>
      <Button onClick={() => void router.navigate({ to: "/workbench" })}>
        {t("backToWorkbench")}
      </Button>
    </div>
  );
}

function RootComponent() {
  return <Outlet />;
}
